/**
 * HRIS Jobs API — TanStack Query hooks for /v4/job-postings.
 *
 * API shape mirrors apps/rest_server/routes/v4/job-postings.js and the
 * Drizzle schema in apps/rest_server/drizzle/schema/job-postings.ts.
 * All field names are camelCase (Drizzle serialises them that way).
 * No If-Match — last-write-wins (spec §13, v1 scope).
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { api } from '@/lib/api';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type JobStatus = 'draft' | 'open' | 'closed';
export type DepartmentIcon = 'sales' | 'operations';
export type RemoteType = 'onsite' | 'hybrid' | 'fully_remote';

/** Shape returned by GET /v4/job-postings and GET /v4/job-postings/:id */
export interface JobPosting {
  id: string;
  slug: string;
  visible: boolean;
  status: JobStatus;
  departmentIcon: DepartmentIcon;
  titleEn: string;
  titleFr: string;
  departmentEn: string | null;
  departmentFr: string | null;
  city: string;
  state: string;
  country: string;
  remoteTypeVal: RemoteType | null;
  typeEn: string;
  typeFr: string;
  reportsToEn: string | null;
  reportsToFr: string | null;
  summaryEn: string;
  summaryFr: string;
  responsibilitiesEn: string[];
  responsibilitiesFr: string[];
  requirementsEn: string[];
  requirementsFr: string[];
  niceToHaveEn: string[];
  niceToHaveFr: string[];
  benefitsEn: string[];
  benefitsFr: string[];
  salary: string | null;
  datePosted: string;
  expiresAt: string | null;
  createdAt: string;
  updatedAt: string;
}

/** Shape for GET /v4/job-postings list response */
interface JobPostingsListResponse {
  jobPostings: JobPosting[];
  total: number;
}

export interface JobPostingFilters {
  status?: JobStatus;
  visible?: boolean;
  search?: string;
}

/** Mutable fields for create / update */
export type CreateJobPostingInput = Omit<JobPosting, 'id' | 'createdAt' | 'updatedAt'>;
export type UpdateJobPostingInput = Partial<CreateJobPostingInput>;

// ---------------------------------------------------------------------------
// Query key factory
// ---------------------------------------------------------------------------

export const jobPostingKeys = {
  all: ['job-postings'] as const,
  lists: () => [...jobPostingKeys.all, 'list'] as const,
  list: (filters?: JobPostingFilters) => [...jobPostingKeys.lists(), filters] as const,
  details: () => [...jobPostingKeys.all, 'detail'] as const,
  detail: (id: string) => [...jobPostingKeys.details(), id] as const,
};

// ---------------------------------------------------------------------------
// API functions
// ---------------------------------------------------------------------------

async function fetchJobPostings(filters: JobPostingFilters = {}): Promise<JobPostingsListResponse> {
  const params: Record<string, string> = {};
  if (filters.status) params.status = filters.status;
  if (filters.visible !== undefined) params.visible = String(filters.visible);
  if (filters.search) params.search = filters.search;
  return api.get<JobPostingsListResponse>('/v4/job-postings', params);
}

async function fetchJobPosting(id: string): Promise<JobPosting> {
  return api.get<JobPosting>(`/v4/job-postings/${id}`);
}

async function createJobPosting(data: CreateJobPostingInput): Promise<JobPosting> {
  return api.post<JobPosting>('/v4/job-postings', data);
}

async function updateJobPosting(id: string, data: UpdateJobPostingInput): Promise<JobPosting> {
  return api.patch<JobPosting>(`/v4/job-postings/${id}`, data);
}

// ---------------------------------------------------------------------------
// Error utility
// ---------------------------------------------------------------------------

function getErrorMessage(error: unknown): string {
  if (error instanceof Error) return error.message;
  if (typeof error === 'object' && error !== null && 'message' in error) {
    return String((error as { message: unknown }).message);
  }
  return 'Une erreur inattendue est survenue';
}

// ---------------------------------------------------------------------------
// Hooks
// ---------------------------------------------------------------------------

export function useJobPostings(filters: JobPostingFilters = {}) {
  return useQuery({
    queryKey: jobPostingKeys.list(filters),
    queryFn: () => fetchJobPostings(filters),
  });
}

export function useJobPosting(id: string) {
  return useQuery({
    queryKey: jobPostingKeys.detail(id),
    queryFn: () => fetchJobPosting(id),
    enabled: !!id,
  });
}

export function useCreateJobPosting() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: CreateJobPostingInput) => createJobPosting(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: jobPostingKeys.lists() });
      toast.success('Poste créé avec succès');
    },
    onError: (error) => {
      toast.error('Erreur lors de la création du poste', { description: getErrorMessage(error) });
    },
  });
}

export function useUpdateJobPosting() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateJobPostingInput }) =>
      updateJobPosting(id, data),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: jobPostingKeys.detail(id) });
      queryClient.invalidateQueries({ queryKey: jobPostingKeys.lists() });
    },
    onError: (error) => {
      toast.error('Erreur lors de la mise à jour', { description: getErrorMessage(error) });
    },
  });
}
